import { useId } from 'react';
import BaseSelectOption, { ActionMeta } from 'react-select';
import { useDebounce } from '@trukt/react-hooks/useDebounce';
import { Option, OptionValue, OptionRaw } from '@trukt/portal-utils/Option';
import * as Components from './components';
import { SelectOptionProps } from './SelectOption.types';
import { filterOption } from './SelectOption.helpers';
import { useValue } from './SelectOption.hooks';
import { useStyles } from './SelectOption.styles';

const EMPTY_OPTION: Option[] = [];

export const SelectOption = <Value extends OptionValue = string, Raw extends OptionRaw = unknown>({
  onDebouncedInputChange = () => {},
  ...rest
}: SelectOptionProps<Value, Raw>) => {
  const debouncedInputChange = useDebounce(onDebouncedInputChange);
  const id = useId();
  const { options = EMPTY_OPTION, selectedValueId, name } = rest;
  const accessibleId = (typeof rest.label === 'string' && rest.label) || name || id;
  const styles = useStyles(rest);

  const value = useValue<Value, Raw>(options as Option<Value, Raw>[], selectedValueId);

  const variant = rest.variant ?? 'default';
  const isClearable = variant === 'search' || rest.isClearable;

  const props = {
    ...rest,
    variant,
    onChange: rest.onChange as
      ((newValue: unknown, actionMeta: ActionMeta<unknown>) => void) | undefined,
  };

  const handleInputChange: SelectOptionProps['onInputChange'] = (newValue, actionMeta) => {
    if (actionMeta.action !== 'input-change') return;

    if (props.onInputChange) {
      props.onInputChange(newValue, actionMeta);
      return;
    }

    debouncedInputChange(newValue, actionMeta);
  };

  const noOptionsMessage = ({ inputValue }: { inputValue: string }) =>
    !rest.isLoading && inputValue.length === 0 && 'Type to begin search';

  return (
    <div className={props.classNames?.root} data-testid={rest.testId}>
      <div
        css={styles.selectOptionContainer}
        data-testid="select_option__root"
        data-value={value?.value}
        data-label={value?.label}
      >
        <BaseSelectOption
          {...props}
          inputId={accessibleId}
          aria-label={rest['aria-label'] ?? accessibleId}
          components={{ ...Components, IndicatorSeparator: null }}
          value={rest.value ?? value}
          filterOption={filterOption}
          isClearable={isClearable}
          onInputChange={handleInputChange}
          noOptionsMessage={noOptionsMessage}
          /**
           * component is always searchable as it gives users the ability to narrow down large pick lists
           */
          isSearchable
          placeholder={props.placeholder ?? null}
        />
      </div>
    </div>
  );
};
